import React, {useEffect} from "react";
import {Link, Redirect, useLocation} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {getSyncEngineVersion} from "../../../common/helpers/syncEngine";
import {RouteDefine} from "../../../routes/RouteDefine";
import {getSettingsEconomicPath} from "../../../routes/settingsPaths";
import SettingsPageHeader from "../Settings/components/SettingsPageHeader";
import {HelpMockup} from "./components/HelpMockups";

type HelpSection = {
    id: string;
    titleKey: string;
    bodyKey: string;
    setupKey?: string;
    extraLink?: {to: string; labelKey: string};
};

const CONCEPT_SECTIONS: HelpSection[] = [
    {id: "accounting-mode", titleKey: "help.conceptAccountingTitle", bodyKey: "help.conceptAccountingBody", extraLink: {
        to: `${RouteDefine.settingsEconomic}?setup=common`,
        labelKey: "help.whereAccountingMode",
    }},
    {id: "draft-vs-booked", titleKey: "help.conceptDraftTitle", bodyKey: "help.conceptDraftBody"},
    {id: "matching", titleKey: "help.conceptMatchingTitle", bodyKey: "help.conceptMatchingBody"},
    {id: "notice", titleKey: "help.conceptNoticeTitle", bodyKey: "help.conceptNoticeBody"},
    {id: "modules-toggle", titleKey: "help.conceptModulesTitle", bodyKey: "help.conceptModulesBody"},
    {id: "schedule", titleKey: "help.conceptScheduleTitle", bodyKey: "help.conceptScheduleBody", extraLink: {
        to: RouteDefine.settingsSchedule,
        labelKey: "help.whereSchedule",
    }},
];

const MODULE_SECTIONS: HelpSection[] = [
    {id: "common", titleKey: "help.commonTitle", bodyKey: "help.commonBody", setupKey: "common"},
    {id: "invoice", titleKey: "help.invoiceTitle", bodyKey: "help.invoiceBody", setupKey: "invoice"},
    {id: "refund", titleKey: "help.refundTitle", bodyKey: "help.refundBody", setupKey: "refund"},
    {id: "payment", titleKey: "help.paymentTitle", bodyKey: "help.paymentBody", setupKey: "payment"},
    {id: "payout", titleKey: "help.payoutTitle", bodyKey: "help.payoutBody", setupKey: "payout"},
    {id: "subscription", titleKey: "help.subscriptionTitle", bodyKey: "help.subscriptionBody", setupKey: "subscription"},
];

const Help: React.FC = () => {
    const {t} = useTranslation();
    const location = useLocation();
    const isV2 = getSyncEngineVersion() === "v2";

    useEffect(() => {
        if (!location.hash) {
            return;
        }
        const id = location.hash.replace("#", "");
        const el = document.getElementById(id);
        if (el) {
            el.scrollIntoView({behavior: "smooth", block: "start"});
        }
    }, [location.hash]);

    if (!isV2) {
        return <Redirect to={RouteDefine.dashboard}/>;
    }

    const economicPath = getSettingsEconomicPath();
    const tocItems = [
        {id: "getting-started", label: t("help.gettingStartedTitle")},
        {id: "concepts", label: t("help.conceptsTitle")},
        {id: "modules", label: t("help.modulesTitle")},
        {id: "where", label: t("help.whereTitle")},
    ];

    const renderParagraphs = (text: string) =>
        text.split("\n").map((line, index) => (
            <p key={index} className="text-muted mb-2 small">
                {line}
            </p>
        ));

    return (
        <React.Fragment>
            <div className="row" id="help-top">
                <SettingsPageHeader
                    title={t("help.pageTitle")}
                    subtitle={t("help.pageSubtitle")}
                    showBackToDashboard
                />
            </div>

            <div className="row">
                <div className="col-lg-3 mb-3">
                    <div className="business-detail bg-orange-bland-zp bdr-20 pd-25 sticky-top" style={{top: 80}}>
                        <h2 className="fw-bold fs-18 mb-3">{t("help.tocTitle")}</h2>
                        <ul className="list-unstyled mb-0">
                            {tocItems.map((item) => (
                                <li key={item.id} className="mb-2">
                                    <a href={`#${item.id}`} className="text-decoration-none">
                                        {item.label}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                <div className="col-lg-9">
                    <section id="getting-started" className="business-detail bg-orange-bland-zp bdr-20 pd-25 mb-3">
                        <h2 className="fw-bold fs-20 mb-3">{t("help.gettingStartedTitle")}</h2>
                        {renderParagraphs(t("help.gettingStartedBody"))}
                        <HelpMockup sectionId="getting-started"/>
                        <div className="d-flex flex-wrap gap-2 mt-3">
                            <Link to={RouteDefine.settings} className="btn btn-sm btn-primary">
                                {t("help.gettingStartedCtaSettings")}
                            </Link>
                            <Link to={RouteDefine.dashboard} className="btn btn-sm btn-light">
                                {t("help.gettingStartedCtaDashboard")}
                            </Link>
                        </div>
                    </section>

                    <section id="concepts" className="mb-3">
                        <h2 className="fw-bold fs-20 mb-3">{t("help.conceptsTitle")}</h2>
                        {CONCEPT_SECTIONS.map((section) => (
                            <div
                                key={section.id}
                                id={section.id}
                                className="business-detail bg-orange-bland-zp bdr-20 pd-25 mb-3"
                            >
                                <h3 className="fw-bold fs-18 mb-2">{t(section.titleKey)}</h3>
                                {renderParagraphs(t(section.bodyKey))}
                                <HelpMockup sectionId={section.id}/>
                                {section.extraLink ? (
                                    <Link to={section.extraLink.to} className="btn btn-sm btn-link px-0 mt-2">
                                        {t(section.extraLink.labelKey)} →
                                    </Link>
                                ) : null}
                            </div>
                        ))}
                    </section>

                    <section id="modules" className="mb-3">
                        <h2 className="fw-bold fs-20 mb-3">{t("help.modulesTitle")}</h2>
                        {MODULE_SECTIONS.map((section) => (
                            <div
                                key={section.id}
                                id={section.id}
                                className="business-detail bg-orange-bland-zp bdr-20 pd-25 mb-3"
                            >
                                <div className="d-flex flex-column flex-md-row align-items-md-start justify-content-between gap-2">
                                    <div className="flex-grow-1">
                                        <h3 className="fw-bold fs-18 mb-2">{t(section.titleKey)}</h3>
                                        {renderParagraphs(t(section.bodyKey))}
                                        <HelpMockup sectionId={section.id}/>
                                    </div>
                                    {section.setupKey ? (
                                        <Link
                                            to={`${economicPath}?setup=${section.setupKey}`}
                                            className="btn btn-sm btn-primary flex-shrink-0"
                                        >
                                            {t("help.openSection")}
                                        </Link>
                                    ) : null}
                                </div>
                            </div>
                        ))}
                    </section>

                    <section id="where" className="business-detail bg-orange-bland-zp bdr-20 pd-25 mb-3">
                        <h2 className="fw-bold fs-20 mb-3">{t("help.whereTitle")}</h2>
                        <ul className="list-unstyled mb-0">
                            <li className="mb-2">
                                <Link to={economicPath}>{t("help.whereEconomic")}</Link>
                            </li>
                            <li className="mb-2">
                                <Link to={RouteDefine.settingsSchedule}>{t("help.whereSchedule")}</Link>
                            </li>
                            <li className="mb-2">
                                <Link to={RouteDefine.invoice}>{t("help.whereLogs")}</Link>
                            </li>
                            <li className="mb-2">
                                <Link to={RouteDefine.dashboard}>{t("help.whereDashboard")}</Link>
                            </li>
                        </ul>
                        <a href="#help-top" className="small d-inline-block mt-3">
                            {t("help.backToTop")}
                        </a>
                    </section>
                </div>
            </div>
        </React.Fragment>
    );
};

export default React.memo(Help);
